"use client";

import Image from "next/image";
import Link from "next/link";
import { useLanguage } from "@/hooks/useLanguage";
import { cn } from "@/lib/utils";
import type { SponsoredHolding } from "./types";
import {
	RANK_META,
	buildTokenImageUrl,
	formatHoldingBalanceCompact,
	formatHoldingBalanceFull,
	formatHoldingUsdValue,
	formatHoldingUsdValueFull,
	getSupportAgeToneClass,
	formatSupportAgeLabel,
	formatTokenPrice,
} from "./utils";

interface RankingItemProps {
	holding: SponsoredHolding;
	rank: number;
	active: boolean;
	onClick: () => void;
	href?: string;
	btcPrice: number;
	showSupportAge?: boolean;
}

function truncateLabel(text: string, maxLength = 12) {
	if (text.length <= maxLength) return text;
	return `${text.slice(0, maxLength)}...`;
}

export function RankingItem({
	holding,
	rank,
	active,
	onClick,
	href,
	btcPrice,
	showSupportAge = rank < 3,
}: RankingItemProps) {
	const medal = RANK_META[Math.min(rank, RANK_META.length - 1)] ?? RANK_META[2];
	const { getTranslation, language } = useLanguage();

	const tokenSummary = (
		<>
			<div className="relative h-12 w-12 shrink-0">
				<div className="relative h-12 w-12 overflow-hidden rounded-full border border-white/60 bg-light-surface shadow-[0_8px_20px_rgba(15,23,42,0.08)] dark:bg-dark-surface dark:shadow-[0_10px_24px_rgba(0,0,0,0.28)]">
					<Image
						src={buildTokenImageUrl(holding.token.id)}
						alt={holding.token.name}
						fill
						sizes="48px"
						className="object-cover"
					/>
				</div>
				<div
					className={cn(
						"absolute -left-1 -top-1 flex h-[22px] w-[22px] items-center justify-center rounded-full border border-white/80 text-[10px] font-black leading-none text-slate-900 shadow-[0_8px_18px_rgba(15,23,42,0.18)] ring-1 ring-black/5 dark:border-white/20 dark:ring-white/10",
						active && "shadow-[0_10px_24px_rgba(15,23,42,0.24)]",
					)}
					style={{
						background: `linear-gradient(135deg, ${medal.colors[0]} 0%, ${medal.colors[1]} 100%)`,
					}}
					aria-label={medal.badge}
				>
					<span className="absolute inset-[1px] rounded-full bg-[radial-gradient(circle_at_30%_28%,rgba(255,255,255,0.72),transparent_42%)]" />
					<span className="relative z-10">{rank + 1}</span>
				</div>
			</div>
			<div className="min-w-0 flex-1">
				<div className="flex items-center gap-2">
					<p className="block max-w-[12ch] overflow-hidden whitespace-nowrap text-sm font-bold text-light-text dark:text-dark-text">
						{truncateLabel(holding.token.ticker)}
					</p>
				</div>
				<p className="block max-w-[12ch] overflow-hidden whitespace-nowrap text-xs text-light-accent dark:text-dark-accent">
					{truncateLabel(holding.token.name)}
				</p>
				<div className="mt-2 flex items-center gap-3 text-[11px] text-gray-500 dark:text-gray-400">
					<span>{formatTokenPrice(holding.token)}</span>
				</div>
			</div>
		</>
	);

	return (
		<div
			onClick={onClick}
			className={cn(
				"group relative flex w-full items-center gap-3 rounded-[22px] border p-3 text-left transition-all duration-300 cursor-pointer",
				active
					? "border-transparent bg-light-surface shadow-lg ring-1 ring-light-primary/15 dark:bg-dark-surface dark:ring-dark-primary/20"
					: "border-light-border/70 bg-light-background/80 hover:bg-light-surface hover:shadow-md dark:border-dark-border/70 dark:bg-dark-background/80 dark:hover:bg-dark-surface",
			)}
			aria-pressed={active}
			role="button"
			tabIndex={0}
			onKeyDown={(event) => {
				if (event.key === "Enter" || event.key === " ") {
					event.preventDefault();
					onClick();
				}
			}}
			>
				<div
					className="absolute inset-y-3 left-0 w-[3px] rounded-full"
					style={{
						background: `linear-gradient(180deg, ${medal.colors[0]} 0%, ${medal.colors[1]} 100%)`,
						opacity: active ? 0.78 : 0.42,
					}}
				/>
				{href ? (
					<Link
						href={href}
						target="_blank"
						rel="noreferrer"
						onClick={(event) => event.stopPropagation()}
						className="flex min-w-0 flex-[1.35] cursor-pointer items-center gap-3 rounded-[18px] pl-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-light-accent/60 dark:focus-visible:ring-dark-accent/60"
					>
						{tokenSummary}
					</Link>
				) : (
					<div className="flex min-w-0 flex-[1.35] items-center gap-3 pl-1">{tokenSummary}</div>
				)}
			<div className="min-w-[82px] text-right">
				<div className="flex items-baseline justify-end gap-1.5">
					<p className="text-[11px] uppercase tracking-wide text-light-accent dark:text-dark-accent">
						{getTranslation("plaza.valuation")}
					</p>
					<p
						className="font-semibold text-light-text dark:text-dark-text"
						title={formatHoldingUsdValueFull(holding, btcPrice)}
					>
						{formatHoldingUsdValue(holding, btcPrice)}
					</p>
				</div>
				<div className="mt-1 flex items-baseline justify-end gap-1.5">
					<p className="text-[11px] uppercase tracking-wide text-light-accent dark:text-dark-accent">
						{getTranslation("plaza.holders")}
					</p>
					<p
						className="text-xs font-medium text-gray-500 dark:text-gray-400"
						title={formatHoldingBalanceFull(holding)}
					>
						{formatHoldingBalanceCompact(holding)}
					</p>
				</div>
				{showSupportAge ? (
					<p
						className={cn(
							"mt-1 text-[11px] font-medium",
							getSupportAgeToneClass(holding.supportAgeDays),
						)}
					>
						{formatSupportAgeLabel(holding.supportAgeDays, language)}
					</p>
				) : null}
			</div>
		</div>
	);
}
